import React, { useState, useEffect, useCallback } from 'react';
import {
  Camera, Search, RefreshCw, X, ZoomIn, Trash2,
  AlertTriangle, CheckCircle, Clock, User, ScanLine,
  ChevronLeft, ChevronRight, Download,
} from 'lucide-react';

const API = '/.netlify/functions/scanner-photo';

async function api(body) {
  const res = await fetch(API, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function iso30daysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

// ── Flag badge ────────────────────────────────────────────────────────────────
function FlagBadge({ flags }) {
  if (!flags) return null;
  const items = [];

  if (flags.hasUnsentIssue) {
    items.push(
      <span key="unsent" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: 10, letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <AlertTriangle size={10} /> UNSENT {flags.unsentData}
      </span>
    );
  }
  if (flags.hasTasksRemaining) {
    items.push(
      <span key="tasks" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: 10, letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <ScanLine size={10} /> TASKS {flags.taskListCount}
      </span>
    );
  }
  if (items.length === 0 && flags.unsentData !== null && flags.unsentData !== undefined) {
    items.push(
      <span key="ok" style={{ background: 'rgba(52,211,153,0.12)', color: '#34d399', fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: 10, letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <CheckCircle size={10} /> CLEAR
      </span>
    );
  }
  return <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{items}</div>;
}

// ── Photo card ────────────────────────────────────────────────────────────────
function PhotoCard({ photo, onView, onDelete }) {
  const hasIssue = photo.flags?.hasIssues;
  return (
    <div style={{
      background: 'var(--hp-card)',
      border: `1px solid ${hasIssue ? 'rgba(239,68,68,0.35)' : 'var(--hp-border)'}`,
      borderRadius: 'var(--radius)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transition: 'border-color 0.15s',
    }}>
      {/* Thumbnail — loads directly from Google Drive */}
      <div
        onClick={() => onView(photo)}
        style={{
          height: 140, overflow: 'hidden',
          background: hasIssue ? 'rgba(239,68,68,0.06)' : 'var(--hp-surface)',
          cursor: 'pointer', position: 'relative',
          borderBottom: '1px solid var(--hp-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {photo.thumbUrl ? (
          <img
            src={photo.thumbUrl}
            alt="Scanner"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--hp-dim)' }}>
            <Camera size={28} />
            <div style={{ fontSize: '0.72rem', marginTop: 6 }}>Tap to view</div>
          </div>
        )}
        {hasIssue && (
          <div style={{ position: 'absolute', top: 8, right: 8, background: '#ef4444', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertTriangle size={10} color="#fff" />
          </div>
        )}
      </div>

      {/* Meta */}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--hp-text)' }}>
              {photo.driverName || photo.driver}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <Clock size={10} /> {fmtDateTime(photo.uploadedAt)}
            </div>
          </div>
          <button
            onClick={() => onDelete(photo)}
            style={{ background: 'none', border: 'none', color: 'var(--hp-muted)', cursor: 'pointer', padding: 2, borderRadius: 4 }}
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        </div>

        <FlagBadge flags={photo.flags} />

        {photo.flags?.screenDriver && photo.flags.screenDriver !== photo.driver && (
          <div style={{ fontSize: '0.7rem', color: '#f59e0b' }}>
            Scanner: {photo.flags.screenDriver}
          </div>
        )}

        {photo.note && (
          <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', fontStyle: 'italic', borderTop: '1px solid var(--hp-border)', paddingTop: 6, marginTop: 2 }}>
            "{photo.note}"
          </div>
        )}
      </div>
    </div>
  );
}

// ── Photo modal ───────────────────────────────────────────────────────────────
function PhotoModal({ photo, photos, onClose, onDelete }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError]   = useState(false);
  const currentIdx = photos.findIndex(p => p.key === photo.key);
  const [current, setCurrent] = useState(photo);

  useEffect(() => { setImgLoaded(false); setImgError(false); }, [current.key]);

  // Build full-size URL from thumbUrl (replace w600 with w1600) — more reliable than viewUrl
  const fullSizeUrl = current.thumbUrl
    ? current.thumbUrl.replace('sz=w600', 'sz=w1600')
    : current.viewUrl;

  const prev = () => { if (currentIdx > 0) setCurrent(photos[currentIdx - 1]); };
  const next = () => { if (currentIdx < photos.length - 1) setCurrent(photos[currentIdx + 1]); };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const downloadPhoto = () => {
    if (!current.viewUrl) return;
    const a = document.createElement('a');
    a.href = current.viewUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 400, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', background: 'var(--hp-card)', borderBottom: '1px solid var(--hp-border)', flexShrink: 0 }}>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--hp-text)', fontSize: '0.95rem' }}>
            {current.driverName || current.driver}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>
            {fmtDateTime(current.uploadedAt)}
            {current.note && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>· {current.note}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FlagBadge flags={current.flags} />
          <button onClick={downloadPhoto} style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '6px 10px', color: 'var(--hp-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem' }}>
            <Download size={13} /> Save
          </button>
          <button onClick={() => { onDelete(current); onClose(); }} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '6px 10px', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem' }}>
            <Trash2 size={13} /> Delete
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', marginLeft: 4 }}>
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
        {/* Prev */}
        {currentIdx > 0 && (
          <button onClick={prev} style={{ position: 'absolute', left: 16, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
            <ChevronLeft size={20} />
          </button>
        )}

        {!imgLoaded && !imgError && (
          <div style={{ color: 'var(--hp-dim)', textAlign: 'center' }}>
            <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
            <div style={{ marginTop: 8, fontSize: '0.85rem' }}>Loading…</div>
          </div>
        )}
        {imgError && (
          <div style={{ textAlign: 'center', color: '#ef4444', fontSize: '0.9rem' }}>
            <p>Could not load image.</p>
            <a href={current.viewUrl} target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--hp-gold)', fontSize: '0.8rem' }}>
              Open in Google Drive ↗
            </a>
          </div>
        )}
        {fullSizeUrl && (
          <img
            src={fullSizeUrl}
            alt="Scanner"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', borderRadius: 4, display: imgLoaded ? 'block' : 'none' }}
          />
        )}

        {/* Next */}
        {currentIdx < photos.length - 1 && (
          <button onClick={next} style={{ position: 'absolute', right: 16, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
            <ChevronRight size={20} />
          </button>
        )}
      </div>

      {/* OCR text panel */}
      {current.ocrText && (
        <div style={{ padding: '10px 20px', background: 'rgba(0,0,0,0.7)', borderTop: '1px solid var(--hp-border)', flexShrink: 0, maxHeight: 100, overflowY: 'auto' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>OCR Text</div>
          <pre style={{ fontSize: '0.75rem', color: 'var(--hp-text)', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-mono)' }}>{current.ocrText}</pre>
        </div>
      )}

      {/* Counter */}
      <div style={{ textAlign: 'center', padding: '8px', color: 'var(--hp-dim)', fontSize: '0.75rem', flexShrink: 0 }}>
        {currentIdx + 1} / {photos.length}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ScannerPhotosPage() {
  const [photos, setPhotos]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [driverFilter, setDriver]   = useState('');
  const [fromDate, setFrom]         = useState(iso30daysAgo());
  const [toDate, setTo]             = useState(isoToday());
  const [viewPhoto, setViewPhoto]   = useState(null);
  const [flagOnly, setFlagOnly]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await api({
        action: 'list',
        driver: driverFilter || undefined,
        from:   fromDate || undefined,
        to:     toDate   || undefined,
      });
      if (!data.ok) throw new Error(data.error);
      setPhotos(data.photos || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [driverFilter, fromDate, toDate]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleDelete = async (photo) => {
    if (!window.confirm(`Delete scanner photo for ${photo.driverName || photo.driver} on ${photo.date}?`)) return;
    try {
      await api({ action: 'delete', key: photo.key });
      setPhotos(prev => prev.filter(p => p.key !== photo.key));
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  const drivers = [...new Set(photos.map(p => p.driver))].sort();
  const displayed = flagOnly ? photos.filter(p => p.flags?.hasIssues) : photos;
  const issueCount = photos.filter(p => p.flags?.hasIssues).length;

  const inputStyle = {
    background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
    borderRadius: 8, padding: '7px 10px', color: 'var(--hp-text)', fontSize: '0.82rem',
    fontFamily: 'var(--font-body)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '0 4px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Camera size={22} /> Scanner Photos
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>
            End-of-shift scanner screenshots — auto-deleted after 30 days
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 7, padding: '7px 14px', color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: 12 }}>
        {[
          { label: 'Total Photos', value: photos.length, color: 'var(--hp-gold)' },
          { label: 'Issues Flagged', value: issueCount, color: issueCount > 0 ? '#ef4444' : 'var(--hp-dim)' },
          { label: 'Drivers', value: drivers.length, color: '#60a5fa' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Driver</label>
          <select value={driverFilter} onChange={e => setDriver(e.target.value)} style={{ ...inputStyle, minWidth: 150 }}>
            <option value="">All drivers</option>
            {drivers.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>From</label>
          <input type="date" value={fromDate} onChange={e => setFrom(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>To</label>
          <input type="date" value={toDate} onChange={e => setTo(e.target.value)} style={inputStyle} />
        </div>
        <button onClick={load} style={{ padding: '7px 18px', background: 'var(--hp-gold)', border: 'none', borderRadius: 8, color: 'var(--hp-black)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-end' }}>
          <Search size={13} /> Search
        </button>
        {issueCount > 0 && (
          <button
            onClick={() => setFlagOnly(f => !f)}
            style={{ padding: '7px 14px', background: flagOnly ? 'rgba(239,68,68,0.15)' : 'var(--hp-surface)', border: `1px solid ${flagOnly ? 'rgba(239,68,68,0.5)' : 'var(--hp-border)'}`, borderRadius: 8, color: flagOnly ? '#ef4444' : 'var(--hp-dim)', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-end' }}
          >
            <AlertTriangle size={13} /> Issues only ({issueCount})
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '12px 16px', color: '#ef4444', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--hp-dim)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading photos…
        </div>
      )}

      {/* Empty */}
      {!loading && !error && displayed.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--hp-dim)' }}>
          <Camera size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: '0.9rem' }}>
            {photos.length === 0 ? 'No scanner photos yet. Drivers submit these from the Driver Portal.' : 'No photos match current filters.'}
          </div>
        </div>
      )}

      {/* Photo grid */}
      {!loading && displayed.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: 14 }}>
          {displayed.map(photo => (
            <PhotoCard
              key={photo.key}
              photo={photo}
              onView={p => setViewPhoto(p)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      {viewPhoto && (
        <PhotoModal
          photo={viewPhoto}
          photos={displayed}
          onClose={() => setViewPhoto(null)}
          onDelete={(p) => { handleDelete(p); setViewPhoto(null); }}
        />
      )}
    </div>
  );
}
